import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import tenantService from "../services/tenantService";
import awsService from "../services/awsService";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import Loading from "../components/Loading";
import { useStateContext } from "../contexts/ContextProvider";

const initialValues = {
  awsAccounts: [
    {
      awsId: "",
      isDefault: true,
      bucketList: [
        {
          bucketName: "",
          isDefault: true,
          folders: { folderName: "", access: "" },
        },
      ],
    },
  ],
  tenantName: "",
  tenantDomain: "",
  tenantEndPoint: "",
};

const validationSchema = Yup.object({
  awsAccounts: Yup.array()
    .of(
      Yup.object({
        awsId: Yup.string().required("AWS ID is required"),
        isDefault: Yup.boolean(),
        bucketList: Yup.array()
          .of(
            Yup.object({
              bucketName: Yup.string().required("Bucket name is required"),
              isDefault: Yup.boolean(),
              folders: Yup.object({
                folderName: Yup.array().required("Folder name is required"),
                access: Yup.string().required("Access level is required"),
              }),
            })
          )
          .min(1, "At least one bucket is required"),
      })
    )
    .required("Aws Data is Required")
    .min(1, "At least one Aws is required"),
  tenantName: Yup.string().required("Tenant Name is required"),
  tenantDomain: Yup.string().required("Tenant Domain is required"),
  tenantEndPoint: Yup.string().required("Tenant Mail Endpoint is required"),
});

const TenantForm = () => {
  const [bucketOptions, setBucketOptions] = useState({});
  const [folderOptions, setFolderOptions] = useState({});
  const [aws, setAws] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedACL, setSelectedACL] = useState(
    location.state?.tenant || null
  );
  const { currentColor, currentMode } = useStateContext();

  const [loading, setloading] = useState(false);

  useEffect(() => {
    // fetchRoles();
    fetchAws();
    if (selectedACL) {
      // fetchUsers(selectedACL.role);
      selectedACL.awsAccounts.forEach((item, index) => {
        fetchBuckets(item.awsId, index);
        item.bucketList.forEach((bitem, bIndex) => {
          fetchFolders(item.awsId, bitem.bucketName, index, bIndex);
        });
      });
    }
  }, [selectedACL]);

  const fetchAws = async () => {
    try {
      const data = await awsService.getawss({});
      console.log(data);
      setAws(data);
    } catch (error) {
      console.error("Error fetching AWS:", error);
    }
  };

  const fetchBuckets = async (awsId, index) => {
    try {
      const data = await awsService.getavailablebuckets({ awsId });
      if (data) {
        setloading(false);
      }
      setBucketOptions((prevOptions) => ({
        ...prevOptions,
        [index]: data,
      }));
    } catch (error) {
      setloading(false);
      console.error("Error fetching buckets:", error);
    }
  };

  const fetchFolders = async (awsId, bucketName, index, bIndex) => {
    try {
      setloading(true);

      let data = await awsService.getavailablefolders({ bucketName, awsId });
      if (data) {
        setloading(false);
      }
      const filteresFolders = data.filter((item) => item.Type === "folder");
      setFolderOptions((prevOptions) => ({
        ...prevOptions,
        [`${index}-${bIndex}`]: filteresFolders,
      }));
    } catch (error) {
      console.error("Error fetching folders:", error);
      setloading(false);
    }
  };

  const handleAwsChange = async (awsId, index, setFieldValue, aws) => {
    console.log(aws);
    setFieldValue(`awsAccounts.${index}.awsId`, awsId);
    setloading(true);
    await fetchBuckets(awsId, index);
  };

  const handleBucketChange = (bucketName, index, bIndex, setFieldValue) => {
    setFieldValue(
      `awsAccounts.${index}.bucketList.${bIndex}.bucketName`,
      bucketName
    );
  };

  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      if (selectedACL) {
        await tenantService.updatetenant(selectedACL._id, values);
      } else {
        await tenantService.addtenant(values);
      }
      setSubmitting(false);
      navigate("/tenantmanagement");
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitting(false);
    }
  };

  const getInitialValues = () => {
    if (selectedACL) {
      return {
        awsAccounts: selectedACL.awsAccounts || [
          {
            awsId: "",
            isDefault: false,
            bucketList: [
              {
                bucketName: "",
                isDefault: false,
                folders: { folderName: "", access: "" },
              },
            ],
          },
        ],
        tenantName: selectedACL.clientName || "",
        tenantDomain: selectedACL.clientCode || "",
        tenantEndPoint: selectedACL.tenantEndPoint || "",
      };
    }
    return initialValues;
  };

  const handleDefaultAwsChange = (index, setFieldValue, values) => {
    values.awsAccounts.forEach((account, i) => {
      setFieldValue(`awsAccounts.${i}.isDefault`, i === index);
    });
  };

  const handleDefaultBucketChange = (index, bIndex, setFieldValue, values) => {
    values.awsAccounts[index].bucketList.forEach((bucket, j) => {
      setFieldValue(
        `awsAccounts.${index}.bucketList.${j}.isDefault`,
        j === bIndex
      );
    });
  };

  return (
    <div>
      {loading && <Loading loading={"fetching"} currentColor={currentColor} />}
      <h1>{selectedACL ? "Edit Tenant" : "Add Tenant"}</h1>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
      >
        {({ values, isSubmitting, setFieldValue }) => {
          console.log(values);
          const getAvailableAwsOptions = (index) => {
            const selectedIds = values.awsAccounts.map(
              (account) => account.awsId
            );

            return aws.filter(
              (account) =>
                !selectedIds.includes(account._id) ||
                selectedIds.indexOf(account._id) === index
            );
          };

          return (
            <Form>
              <div className="form-group">
                <label htmlFor="tenantName">Tenant Name</label>
                <Field type="text" name="tenantName" className="form-control" />
                <ErrorMessage
                  name="tenantName"
                  component="div"
                  className="text-danger"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tenantDomain">Tenant Domain</label>
                <Field
                  type="text"
                  name="tenantDomain"
                  className="form-control"
                />
                <ErrorMessage
                  name="tenantDomain"
                  component="div"
                  className="text-danger"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tenantEndPoint">Tenant Endpoint</label>
                <Field
                  type="text"
                  name="tenantEndPoint"
                  className="form-control"
                />
                <ErrorMessage
                  name="tenantEndPoint"
                  component="div"
                  className="text-danger"
                />
              </div>

              <FieldArray name="awsAccounts">
                {({ push, remove }) => (
                  <div>
                    {values.awsAccounts.map((awsAccount, index) => (
                      <div key={index} className="border p-3 mb-3">
                        <div className="form-group">
                          <label htmlFor={`awsAccounts.${index}.awsId`}>
                            AWS
                          </label>
                          <Field
                            as="select"
                            name={`awsAccounts.${index}.awsId`}
                            className="form-control"
                            onChange={(e) => {
                              console.log(e.target);
                              const selectedAwsId = e.target.value;
                              handleAwsChange(
                                selectedAwsId,
                                index,
                                setFieldValue,
                                awsAccount
                              );
                            }}
                          >
                            <option value="">Select an AWS</option>
                            {getAvailableAwsOptions(index).map((aws) => (
                              <option key={aws._id} value={aws._id}>
                                {aws.accountName}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name={`awsAccounts.${index}.awsId`}
                            component="div"
                            className="text-danger"
                          />
                        </div>

                        <div className="form-check">
                          <Field
                            type="checkbox"
                            name={`awsAccounts.${index}.isDefault`}
                            className="form-check-input"
                            checked={awsAccount.isDefault}
                            onChange={() =>
                              handleDefaultAwsChange(
                                index,
                                setFieldValue,
                                values
                              )
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`awsAccounts.${index}.isDefault`}
                          >
                            Default AWS
                          </label>
                        </div>

                        <FieldArray name={`awsAccounts.${index}.bucketList`}>
                          {({ push, remove }) => (
                            <div>
                              {awsAccount.bucketList.map((bucket, bIndex) => (
                                <div key={bIndex} className="border p-3 mb-3">
                                  <div className="form-group">
                                    <label
                                      htmlFor={`awsAccounts.${index}.bucketList.${bIndex}.bucketName`}
                                    >
                                      Bucket
                                    </label>
                                    <Field
                                      as="select"
                                      name={`awsAccounts.${index}.bucketList.${bIndex}.bucketName`}
                                      className="form-control"
                                      onChange={async (e) => {
                                        const selectedBucket = e.target.value;
                                        await handleBucketChange(
                                          selectedBucket,
                                          index,
                                          bIndex,
                                          setFieldValue
                                        );
                                        await fetchFolders(
                                          values.awsAccounts[index].awsId,
                                          selectedBucket,
                                          index,
                                          bIndex
                                        );
                                      }}
                                    >
                                      <option value="">Select a bucket</option>
                                      {(bucketOptions[index] || []).map(
                                        (bucketName) => (
                                          <option
                                            key={bucketName}
                                            value={bucketName}
                                          >
                                            {bucketName}
                                          </option>
                                        )
                                      )}
                                    </Field>
                                    <ErrorMessage
                                      name={`awsAccounts.${index}.bucketList.${bIndex}.bucketName`}
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>

                                  <div className="form-check">
                                    <Field
                                      type="checkbox"
                                      name={`awsAccounts.${index}.bucketList.${bIndex}.isDefault`}
                                      className="form-check-input"
                                      checked={bucket.isDefault}
                                      onChange={() =>
                                        handleDefaultBucketChange(
                                          index,
                                          bIndex,
                                          setFieldValue,
                                          values
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`awsAccounts.${index}.bucketList.${bIndex}.isDefault`}
                                    >
                                      Default Bucket
                                    </label>
                                  </div>

                                  <FieldArray
                                    name={`awsAccounts.${index}.bucketList.${bIndex}.folders`}
                                  >
                                    {({ push, remove }) => (
                                      <div>
                                        <div className="border p-3 mb-3">
                                          <div className="form-group">
                                            <label
                                              htmlFor={`awsAccounts.${index}.bucketList.${bIndex}.folders.folderName`}
                                            >
                                              Folder Name
                                            </label>
                                            <Select
                                              isMulti
                                              name={`awsAccounts.${index}.bucketList.${bIndex}.folders.folderName`}
                                              options={(
                                                folderOptions[
                                                  `${index}-${bIndex}`
                                                ] || []
                                              ).map((folder) => ({
                                                value: folder.Prefix,
                                                label: folder.Prefix,
                                              }))}
                                              className="basic-multi-select"
                                              classNamePrefix="select"
                                              onChange={(selectedOptions) => {
                                                const selectedFolders =
                                                  selectedOptions
                                                    ? selectedOptions.map(
                                                        (option) => option.value
                                                      )
                                                    : [];
                                                setFieldValue(
                                                  `awsAccounts.${index}.bucketList.${bIndex}.folders.folderName`,
                                                  selectedFolders
                                                );
                                              }}
                                              value={(
                                                values.awsAccounts[index]
                                                  ?.bucketList[bIndex]?.folders
                                                  ?.folderName || []
                                              ).map((folder) => ({
                                                value: folder,
                                                label: folder,
                                              }))}
                                            />
                                            <ErrorMessage
                                              name={`awsAccounts.${index}.bucketList.${bIndex}.folders.folderName`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>

                                          <div className="form-group">
                                            <label
                                              htmlFor={`awsAccounts.${index}.bucketList.${bIndex}.folders.access`}
                                            >
                                              Access
                                            </label>
                                            <Field
                                              as="select"
                                              name={`awsAccounts.${index}.bucketList.${bIndex}.folders.access`}
                                              className="form-control"
                                            >
                                              <option value="">
                                                Select access
                                              </option>
                                              <option value="read">Read</option>
                                              <option value="write">
                                                Write
                                              </option>
                                              <option value="read-write">
                                                Read-Write
                                              </option>
                                            </Field>
                                            <ErrorMessage
                                              name={`awsAccounts.${index}.bucketList.${bIndex}.folders.access`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </FieldArray>
                                  <Button
                                    variant="danger"
                                    onClick={() => remove(bIndex)}
                                    className="mt-2"
                                  >
                                    Remove Bucket
                                  </Button>
                                </div>
                              ))}
                              <Button
                                variant="secondary"
                                onClick={() =>
                                  push({
                                    bucketName: "",
                                    isDefault: false,
                                    folders: { folderName: [], access: "" },
                                  })
                                }
                                className="mt-3"
                              >
                                Add Bucket
                              </Button>
                            </div>
                          )}
                        </FieldArray>
                        <Button
                          variant="danger"
                          onClick={() => remove(index)}
                          className="mt-2"
                        >
                          Remove AWS Account
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="secondary"
                      onClick={() =>
                        push({
                          awsId: "",
                          isDefault: false,
                          bucketList: [
                            {
                              bucketName: "",
                              isDefault: false,
                              folders: { folderName: [], access: "" },
                            },
                          ],
                        })
                      }
                      className="mt-3"
                    >
                      Add AWS Account
                    </Button>
                  </div>
                )}
              </FieldArray>

              <Button type="submit" disabled={isSubmitting} className="mt-3">
                {selectedACL ? "Update" : "Add"}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default TenantForm;

// import React, { useState, useEffect } from "react";
// import { Button } from "react-bootstrap";
// import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
// import * as Yup from "yup";
// import { useParams, useNavigate, useLocation } from "react-router-dom";
// import tenantService from "../services/tenantService";
// import awsService from "../services/awsService";
// import Select from "react-select";
// import "bootstrap/dist/css/bootstrap.min.css";
// import Loading from "../components/Loading";
// import { useStateContext } from "../contexts/ContextProvider";

// const initialValues = {
//   awsAccounts: [
//     {
//       awsId: "",
//       isDefault: true,
//       bucketList: [
//         {
//           bucketName: "",
//           isDefault: true,
//           folders: { folderName: "", access: "" },
//         },
//       ],
//     },
//   ],
//   tenantName: "",
//   tenantDomain: "",
//   tenantEndPoint: "",
// };

// const validationSchema = Yup.object({
//   awsAccounts: Yup.array()
//     .of(
//       Yup.object({
//         awsId: Yup.string().required("AWS ID is required"),
//         isDefault: Yup.boolean(),
//         bucketList: Yup.array()
//           .of(
//             Yup.object({
//               bucketName: Yup.string().required("Bucket name is required"),
//               isDefault: Yup.boolean(),
//               folders: Yup.object({
//                 folderName: Yup.array().required("Folder name is required"),
//                 access: Yup.string().required("Access level is required"),
//               }),
//             })
//           )
//           .min(1, "At least one bucket is required"),
//       })
//     )
//     .required("Aws Data is Required")
//     .min(1, "At least one Aws is required"),
//   tenantName: Yup.string().required("Tenant Name is required"),
//   tenantDomain: Yup.string().required("Tenant Domain is required"),
//   tenantEndPoint: Yup.string().required("Tenant Mail Endpoint is required"),
// });

// const TenantForm = () => {
//   const [bucketOptions, setBucketOptions] = useState({});
//   const [folderOptions, setFolderOptions] = useState({});
//   const [aws, setAws] = useState([]);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const [selectedACL, setSelectedACL] = useState(
//     location.state?.tenant || null
//   );
//   const { currentColor, currentMode } = useStateContext();
//   const [loading, setloading] = useState(false);

//   useEffect(() => {
//     fetchAws();
//     if (selectedACL) {
//       selectedACL.awsAccounts.forEach((item, index) => {
//         fetchBuckets(item.awsId, index);
//         item.bucketList.forEach((bitem, bIndex) => {
//           fetchFolders(item.awsId, bitem.bucketName, index, bIndex);
//         });
//       });
//     }
//   }, [selectedACL]);

//   const fetchAws = async () => {
//     try {
//       const data = await awsService.getawss({});
//       setAws(data);
//     } catch (error) {
//       console.error("Error fetching AWS:", error);
//     }
//   };

//   const fetchBuckets = async (awsId, index) => {
//     try {
//       const data = await awsService.getavailablebuckets({ awsId });
//       setBucketOptions((prevOptions) => ({
//         ...prevOptions,
//         [index]: data,
//       }));
//     } catch (error) {
//       console.error("Error fetching buckets:", error);
//     }
//   };

//   const fetchFolders = async (awsId, bucketName, index, bIndex) => {
//     try {
//       setloading(true);
//       let data = await awsService.getavailablefolders({ bucketName, awsId });
//       if (data) {
//         setloading(false);
//       }
//       const filteredFolders = data.filter((item) => item.Type === "folder");
//       setFolderOptions((prevOptions) => ({
//         ...prevOptions,
//         [`${index}-${bIndex}`]: filteredFolders,
//       }));
//     } catch (error) {
//       console.error("Error fetching folders:", error);
//     }
//   };

//   const handleAwsChange = (awsId, index, setFieldValue) => {
//     setFieldValue(`awsAccounts.${index}.awsId`, awsId);
//     fetchBuckets(awsId, index);
//   };

//   const handleBucketChange = (bucketName, index, bIndex, setFieldValue) => {
//     setFieldValue(
//       `awsAccounts.${index}.bucketList.${bIndex}.bucketName`,
//       bucketName
//     );
//   };

//   const handleFormSubmit = async (values, { setSubmitting }) => {
//     try {
//       if (selectedACL) {
//         await tenantService.updatetenant(selectedACL._id, values);
//       } else {
//         await tenantService.addtenant(values);
//       }
//       setSubmitting(false);
//       navigate("/tenantmanagement");
//     } catch (error) {
//       console.error("Error submitting form:", error);
//       setSubmitting(false);
//     }
//   };

//   const getInitialValues = () => {
//     if (selectedACL) {
//       return {
//         awsAccounts: selectedACL.awsAccounts || [
//           {
//             awsId: "",
//             isDefault: false,
//             bucketList: [
//               {
//                 bucketName: "",
//                 isDefault: false,
//                 folders: { folderName: "", access: "" },
//               },
//             ],
//           },
//         ],
//         tenantName: selectedACL.tenantName || "",
//         tenantDomain: selectedACL.tenantDomain || "",
//         tenantEndPoint: selectedACL.tenantEndPoint || "",
//       };
//     }
//     return initialValues;
//   };

//   const handleDefaultAwsChange = (index, setFieldValue, values) => {
//     values.awsAccounts.forEach((account, i) => {
//       setFieldValue(`awsAccounts.${i}.isDefault`, i === index);
//     });
//   };

//   const handleDefaultBucketChange = (index, bIndex, setFieldValue, values) => {
//     values.awsAccounts[index].bucketList.forEach((bucket, j) => {
//       setFieldValue(
//         `awsAccounts.${index}.bucketList.${j}.isDefault`,
//         j === bIndex
//       );
//     });
//   };

//   return (
//     <div>
//       {loading && <Loading loading={"fetching"} currentColor={currentColor} />}
//       <h1>{selectedACL ? "Edit Tenant" : "Add Tenant"}</h1>
//       <Formik
//         initialValues={getInitialValues()}
//         validationSchema={validationSchema}
//         onSubmit={handleFormSubmit}
//         enableReinitialize
//       >
//         {({ values, isSubmitting, setFieldValue }) => {
//           const getAvailableAwsOptions = (index) => {
//             const selectedIds = values.awsAccounts.map(
//               (account) => account.awsId
//             );

//             return aws.filter(
//               (account) =>
//                 !selectedIds.includes(account._id) ||
//                 selectedIds.indexOf(account._id) === index
//             );
//           };

//           return (
//             <Form>
//               <div className="form-group">
//                 <label htmlFor="tenantName">Tenant Name</label>
//                 <Field type="text" name="tenantName" className="form-control" />
//                 <ErrorMessage
//                   name="tenantName"
//                   component="div"
//                   className="text-danger"
//                 />
//               </div>

//               <div className="form-group">
//                 <label htmlFor="tenantDomain">Tenant Domain</label>
//                 <Field
//                   type="text"
//                   name="tenantDomain"
//                   className="form-control"
//                 />
//                 <ErrorMessage
//                   name="tenantDomain"
//                   component="div"
//                   className="text-danger"
//                 />
//               </div>

//               <div className="form-group">
//                 <label htmlFor="tenantEndPoint">Tenant Endpoint</label>
//                 <Field
//                   type="text"
//                   name="tenantEndPoint"
//                   className="form-control"
//                 />
//                 <ErrorMessage
//                   name="tenantEndPoint"
//                   component="div"
//                   className="text-danger"
//                 />
//               </div>

//               <FieldArray name="awsAccounts">
//                 {({ push, remove }) => (
//                   <div>
//                     {values.awsAccounts.map((awsAccount, index) => (
//                       <div key={index} className="border p-3 mb-3">
//                         <div className="form-group">
//                           <label htmlFor={`awsAccounts.${index}.awsId`}>
//                             AWS
//                           </label>
//                           <Field
//                             as="select"
//                             name={`awsAccounts.${index}.awsId`}
//                             className="form-control"
//                             onChange={(e) => {
//                               const selectedAwsId = e.target.value;
//                               handleAwsChange(
//                                 selectedAwsId,
//                                 index,
//                                 setFieldValue
//                               );
//                             }}
//                           >
//                             <option value="">Select an AWS</option>
//                             {getAvailableAwsOptions(index).map((aws) => (
//                               <option key={aws._id} value={aws._id}>
//                                 {aws.accountName}
//                               </option>
//                             ))}
//                           </Field>
//                           <ErrorMessage
//                             name={`awsAccounts.${index}.awsId`}
//                             component="div"
//                             className="text-danger"
//                           />
//                         </div>

//                         <div className="form-check">
//                           <Field
//                             type="checkbox"
//                             name={`awsAccounts.${index}.isDefault`}
//                             className="form-check-input"
//                             checked={awsAccount.isDefault}
//                             onChange={() =>
//                               handleDefaultAwsChange(
//                                 index,
//                                 setFieldValue,
//                                 values
//                               )
//                             }
//                           />
//                           <label
//                             className="form-check-label"
//                             htmlFor={`awsAccounts.${index}.isDefault`}
//                           >
//                             Default AWS
//                           </label>
//                         </div>

//                         <FieldArray name={`awsAccounts.${index}.bucketList`}>
//                           {({ push, remove }) => (
//                             <div>
//                               {awsAccount.bucketList.map((bucket, bIndex) => (
//                                 <div key={bIndex} className="border p-3 mb-3">
//                                   <div className="form-group">
//                                     <label
//                                       htmlFor={`awsAccounts.${index}.bucketList.${bIndex}.bucketName`}
//                                     >
//                                       Bucket
//                                     </label>
//                                     <Field
//                                       as="select"
//                                       name={`awsAccounts.${index}.bucketList.${bIndex}.bucketName`}
//                                       className="form-control"
//                                       onChange={async (e) => {
//                                         const selectedBucket = e.target.value;
//                                         await handleBucketChange(
//                                           selectedBucket,
//                                           index,
//                                           bIndex,
//                                           setFieldValue
//                                         );
//                                         await fetchFolders(
//                                           values.awsAccounts[index].awsId,
//                                           selectedBucket,
//                                           index,
//                                           bIndex
//                                         );
//                                       }}
//                                     >
//                                       <option value="">Select a bucket</option>
//                                       {(bucketOptions[index] || []).map(
//                                         (bucketName) => (
//                                           <option
//                                             key={bucketName}
//                                             value={bucketName}
//                                           >
//                                             {bucketName}
//                                           </option>
//                                         )
//                                       )}
//                                     </Field>
//                                     <ErrorMessage
//                                       name={`awsAccounts.${index}.bucketList.${bIndex}.bucketName`}
//                                       component="div"
//                                       className="text-danger"
//                                     />
//                                   </div>

//                                   <div className="form-check">
//                                     <Field
//                                       type="checkbox"
//                                       name={`awsAccounts.${index}.bucketList.${bIndex}.isDefault`}
//                                       className="form-check-input"
//                                       checked={bucket.isDefault}
//                                       onChange={() =>
//                                         handleDefaultBucketChange(
//                                           index,
//                                           bIndex,
//                                           setFieldValue,
//                                           values
//                                         )
//                                       }
//                                     />
//                                     <label
//                                       className="form-check-label"
//                                       htmlFor={`awsAccounts.${index}.bucketList.${bIndex}.isDefault`}
//                                     >
//                                       Default Bucket
//                                     </label>
//                                   </div>

//                                   <FieldArray
//                                     name={`awsAccounts.${index}.bucketList.${bIndex}.folders`}
//                                   >
//                                     {({ push, remove }) => (
//                                       <div>
//                                         <div className="border p-3 mb-3">
//                                           <div className="form-group">
//                                             <label
//                                               htmlFor={`awsAccounts.${index}.bucketList.${bIndex}.folders.folderName`}
//                                             >
//                                               Folder Name
//                                             </label>
//                                             <div className="d-flex align-items-center">
//                                               <Select
//                                                 isMulti
//                                                 name={`awsAccounts.${index}.bucketList.${bIndex}.folders.folderName`}
//                                                 options={folderOptions[`${index}-${bIndex}`]?.map((folder) => ({
//                                                   value: folder.Name,
//                                                   label: folder.Name,
//                                                 })) || []}
//                                                 className="basic-multi-select"
//                                                 classNamePrefix="select"
//                                                 onChange={(selectedOptions) => {
//                                                   const selectedFolders =
//                                                     selectedOptions
//                                                       ? selectedOptions.map(
//                                                           (option) =>
//                                                             option.value
//                                                         )
//                                                       : [];
//                                                   setFieldValue(
//                                                     `awsAccounts.${index}.bucketList.${bIndex}.folders.folderName`,
//                                                     selectedFolders
//                                                   );
//                                                 }}
//                                                 value={
//                                                   values.awsAccounts[index]
//                                                     .bucketList[bIndex].folders
//                                                     .folderName
//                                                     ? values.awsAccounts[index]
//                                                         .bucketList[bIndex]
//                                                         .folders.folderName.map(
//                                                         (folder) => ({
//                                                           value: folder,
//                                                           label: folder,
//                                                         })
//                                                       )
//                                                     : []
//                                                 }
//                                               />
//                                               <Button
//                                                 className="ml-2"
//                                                 variant="primary"
//                                                 onClick={() => {
//                                                   const allFolders =
//                                                     folderOptions[
//                                                       `${index}-${bIndex}`
//                                                     ]?.map(
//                                                       (folder) => folder.Name
//                                                     ) || [];
//                                                   setFieldValue(
//                                                     `awsAccounts.${index}.bucketList.${bIndex}.folders.folderName`,
//                                                     allFolders
//                                                   );
//                                                 }}
//                                               >
//                                                 Select All
//                                               </Button>
//                                             </div>
//                                             <ErrorMessage
//                                               name={`awsAccounts.${index}.bucketList.${bIndex}.folders.folderName`}
//                                               component="div"
//                                               className="text-danger"
//                                             />
//                                           </div>

//                                           <div className="form-group">
//                                             <label
//                                               htmlFor={`awsAccounts.${index}.bucketList.${bIndex}.folders.access`}
//                                             >
//                                               Access
//                                             </label>
//                                             <Field
//                                               as="select"
//                                               name={`awsAccounts.${index}.bucketList.${bIndex}.folders.access`}
//                                               className="form-control"
//                                             >
//                                               <option value="">
//                                                 Select access
//                                               </option>
//                                               <option value="read">Read</option>
//                                               <option value="write">
//                                                 Write
//                                               </option>
//                                               <option value="read-write">
//                                                 Read-Write
//                                               </option>
//                                             </Field>
//                                             <ErrorMessage
//                                               name={`awsAccounts.${index}.bucketList.${bIndex}.folders.access`}
//                                               component="div"
//                                               className="text-danger"
//                                             />
//                                           </div>
//                                         </div>
//                                       </div>
//                                     )}
//                                   </FieldArray>
//                                   <Button
//                                     variant="danger"
//                                     onClick={() => remove(bIndex)}
//                                     className="mt-2"
//                                   >
//                                     Remove Bucket
//                                   </Button>
//                                 </div>
//                               ))}
//                               <Button
//                                 variant="secondary"
//                                 onClick={() =>
//                                   push({
//                                     bucketName: "",
//                                     isDefault: false,
//                                     folders: { folderName: [], access: "" },
//                                   })
//                                 }
//                                 className="mt-3"
//                               >
//                                 Add Bucket
//                               </Button>
//                             </div>
//                           )}
//                         </FieldArray>
//                         <Button
//                           variant="danger"
//                           onClick={() => remove(index)}
//                           className="mt-2"
//                         >
//                           Remove AWS Account
//                         </Button>
//                       </div>
//                     ))}
//                     <Button
//                       variant="secondary"
//                       onClick={() =>
//                         push({
//                           awsId: "",
//                           isDefault: false,
//                           bucketList: [
//                             {
//                               bucketName: "",
//                               isDefault: false,
//                               folders: { folderName: [], access: "" },
//                             },
//                           ],
//                         })
//                       }
//                       className="mt-3"
//                     >
//                       Add AWS Account
//                     </Button>
//                   </div>
//                 )}
//               </FieldArray>

//               <Button type="submit" disabled={isSubmitting} className="mt-3">
//                 {selectedACL ? "Update" : "Add"}
//               </Button>
//             </Form>
//           );
//         }}
//       </Formik>
//     </div>
//   );
// };

// export default TenantForm;
